"use client";

import { useState, useEffect } from "react";
import { Clock, Mail, MapPin, MessageSquare, Phone, Send, Smartphone, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
type Language = 'es' | 'en' | 'eu' | 'fr';
const LANGUAGE_STORAGE_KEY = 'wvc_language';
const LANGUAGE_EVENT_NAME = 'wvc-language-change';
function useLanguage(): [Language, (lang: Language) => void] {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored === 'es' || stored === 'en' || stored === 'eu' || stored === 'fr') {
        return stored;
      }
    } catch {
      // ignore
    }
    return 'es';
  });
  useEffect(() => {
    const handleStorage = () => {
      try {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (stored === 'es' || stored === 'en' || stored === 'eu' || stored === 'fr') {
          setLanguageState(stored);
        }
      } catch {
        // ignore
      }
    };
    const handleCustomEvent = (event: Event) => {
      const custom = event as CustomEvent<Language>;
      if (custom.detail && ['es', 'en', 'eu', 'fr'].includes(custom.detail)) {
        setLanguageState(custom.detail);
      } else {
        handleStorage();
      }
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener(LANGUAGE_EVENT_NAME, handleCustomEvent);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(LANGUAGE_EVENT_NAME, handleCustomEvent);
    };
  }, []);
  const setLanguage = (newLang: Language) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
      window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT_NAME, {
        detail: newLang
      }));
    } catch {
      // ignore
    }
    setLanguageState(newLang);
  };
  return [language, setLanguage];
}
const translations = {
  es: {
    eyebrow: "Electro & Más · Bilbao",
    heading: "Presupuesto, respuesta rápida",
    mobileLabel: "Móvil / WhatsApp",
    landlineLabel: "Teléfono fijo",
    emailLabel: "Email",
    workZoneLabel: "Zona de trabajo",
    workZoneValue: "Bilbao, sus alrededores y en todo el País Vasco",
    hoursLabel: "Horario",
    hoursValue: "L-V 8:00-19:00 · Urgencias 24h",
    whatsappBtn: "WhatsApp directo: 662 49 88 28",
    nameLabel: "Nombre",
    namePlaceholder: "Tu nombre completo",
    phoneLabel: "Teléfono",
    phonePlaceholder: "600 000 000",
    emailFieldLabel: "Email",
    emailPlaceholder: "ejemplo@correo.com",
    serviceLabel: "Tipo de servicio (opcional)",
    selectServicePlaceholder: "Selecciona un servicio",
    serviceOptions: ["Domótica Loxone", "Avería / urgencia", "Reforma eléctrica", "Cuadro eléctrico", "Iluminación / LED", "Punto de recarga VE", "Boletín / legalización", "Otro"],
    messageLabel: "Mensaje",
    messagePlaceholder: "Describe detalladamente qué necesitas o en qué podemos ayudarte...",
    submitBtn: "Enviar consulta",
    submittingBtn: "Enviando...",
    consentLabelPrefix: "He leído y acepto la ",
    privacyPolicyText: "Política de Privacidad",
    consentRequiredError: "Debes aceptar la Política de Privacidad para poder enviar la consulta.",
    layerTitle: "Información básica sobre protección de datos",
    layerResponsibleLabel: "Responsable",
    layerResponsibleValue: "Carlos Leandro Claros Villa (Electro & Más)",
    layerPurposeLabel: "Finalidad",
    layerPurposeValue: "atender tu consulta o solicitud de presupuesto",
    layerLegalBasisLabel: "Legitimación",
    layerLegalBasisValue: "tu consentimiento",
    layerRecipientsLabel: "Destinatarios",
    layerRecipientsValue: "no se ceden datos a terceros salvo obligación legal",
    layerRightsLabel: "Derechos",
    layerRightsValue: "acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a electroymasbilbao@gmail.com",
    layerMoreInfo: "Más información en la ",
    successHeading: "¡Consulta enviada!",
    successText: "Hemos recibido tu mensaje correctamente. Nos pondremos en contacto contigo con la mayor brevedad posible.",
    successBtn: "Enviar otra consulta"
  },
  en: {
    eyebrow: "Electro & Más · Bilbao",
    heading: "Quote, fast response",
    mobileLabel: "Mobile / WhatsApp",
    landlineLabel: "Landline",
    emailLabel: "Email",
    workZoneLabel: "Service area",
    workZoneValue: "Bilbao, surrounding areas, and throughout the Basque Country",
    hoursLabel: "Hours",
    hoursValue: "Mon-Fri 8:00-19:00 · 24/7 Emergencies",
    whatsappBtn: "Direct WhatsApp: 662 49 88 28",
    nameLabel: "Name",
    namePlaceholder: "Your full name",
    phoneLabel: "Phone",
    phonePlaceholder: "600 000 000",
    emailFieldLabel: "Email",
    emailPlaceholder: "example@email.com",
    serviceLabel: "Service type (optional)",
    selectServicePlaceholder: "Select a service",
    serviceOptions: ["Loxone Home Automation", "Breakdown / Emergency", "Electrical Renovation", "Electrical Panel", "Lighting / LED", "EV Charging Point", "Electrical Certificate / Legalization", "Other"],
    messageLabel: "Message",
    messagePlaceholder: "Describe in detail what you need or how we can help you...",
    submitBtn: "Send inquiry",
    submittingBtn: "Sending...",
    consentLabelPrefix: "I have read and accept the ",
    privacyPolicyText: "Privacy Policy",
    consentRequiredError: "You must accept the Privacy Policy before submitting your inquiry.",
    layerTitle: "Basic information on data protection",
    layerResponsibleLabel: "Data Controller",
    layerResponsibleValue: "Carlos Leandro Claros Villa (Electro & Más)",
    layerPurposeLabel: "Purpose",
    layerPurposeValue: "to handle your inquiry or quotation request",
    layerLegalBasisLabel: "Legal basis",
    layerLegalBasisValue: "your consent",
    layerRecipientsLabel: "Recipients",
    layerRecipientsValue: "no data is transferred to third parties except under legal obligation",
    layerRightsLabel: "Rights",
    layerRightsValue: "access, rectification, erasure, objection, restriction, and portability by writing to electroymasbilbao@gmail.com",
    layerMoreInfo: "More information in the ",
    successHeading: "Inquiry sent!",
    successText: "We have received your message. We will get in touch with you as soon as possible.",
    successBtn: "Send another inquiry"
  },
  eu: {
    eyebrow: "Electro & Más · Bilbo",
    heading: "Aurrekontua, erantzun azkarra",
    mobileLabel: "Mugikorra / WhatsApp",
    landlineLabel: "Telefono finkoa",
    emailLabel: "Emaila",
    workZoneLabel: "Lan-eremua",
    workZoneValue: "Bilbo, inguruak eta Euskal Herri osoa",
    hoursLabel: "Ordutegia",
    hoursValue: "Al-Ol 8:00-19:00 · 24 orduko larrialdiak",
    whatsappBtn: "Zuzeneko WhatsApp: 662 49 88 28",
    nameLabel: "Izena",
    namePlaceholder: "Zure izen-abizenak",
    phoneLabel: "Telefonoa",
    phonePlaceholder: "600 000 000",
    emailFieldLabel: "Emaila",
    emailPlaceholder: "adibidea@posta.eus",
    serviceLabel: "Zerbitzu mota (aukerakoa)",
    selectServicePlaceholder: "Hautatu zerbitzu bat",
    serviceOptions: ["Loxone Domotika", "Matxura / larrialdia", "Erreforma elektrikoa", "Koadro elektrikoa", "Argiztapena / LED", "IB kargagunea", "Buletina / legeztatzea", "Beste bat"],
    messageLabel: "Mezua",
    messagePlaceholder: "Azaldu xehetasunez zer behar duzun edo nola lagun zaitzakegun...",
    submitBtn: "Bidali kontsulta",
    submittingBtn: "Bidaltzen...",
    consentLabelPrefix: "Irakurri dut eta onartzen dut ",
    privacyPolicyText: "Pribatutasun Politika",
    consentRequiredError: "Pribatutasun Politika onartu behar duzu kontsulta bidaltzeko.",
    layerTitle: "Datuen babesari buruzko oinarrizko informazioa",
    layerResponsibleLabel: "Arduraduna",
    layerResponsibleValue: "Carlos Leandro Claros Villa (Electro & Más)",
    layerPurposeLabel: "Helburua",
    layerPurposeValue: "zure kontsulta edo aurrekontu-eskaera erantzutea",
    layerLegalBasisLabel: "Legitimazioa",
    layerLegalBasisValue: "zure baimena",
    layerRecipientsLabel: "Hartzaileak",
    layerRecipientsValue: "ez zaie daturik lagatzen hirugarrenei legezko betebeharrik ezean",
    layerRightsLabel: "Eskubideak",
    layerRightsValue: "atzipena, zuzenketa, ezabapena, aurkaritza, mugapena eta eramangarritasuna electroymasbilbao@gmail.com helbidera idatziz",
    layerMoreInfo: "Informazio gehiago ",
    successHeading: "Kontsulta bidalita!",
    successText: "Zure mezua ondo jaso dugu. Ahalik eta azkarren jarriko gara zurekin harremanetan.",
    successBtn: "Bidali beste kontsulta bat"
  },
  fr: {
    eyebrow: "Electro & Más · Bilbao",
    heading: "Devis, réponse rapide",
    mobileLabel: "Mobile / WhatsApp",
    landlineLabel: "Téléphone fixe",
    emailLabel: "E-mail",
    workZoneLabel: "Zone d'intervention",
    workZoneValue: "Bilbao, ses environs et tout le Pays Basque",
    hoursLabel: "Horaires",
    hoursValue: "Lun-Ven 8:00-19:00 · Urgences 24h/24",
    whatsappBtn: "WhatsApp direct : 662 49 88 28",
    nameLabel: "Nom",
    namePlaceholder: "Votre nom complet",
    phoneLabel: "Téléphone",
    phonePlaceholder: "600 000 000",
    emailFieldLabel: "E-mail",
    emailPlaceholder: "exemple@courriel.com",
    serviceLabel: "Type de service (optionnel)",
    selectServicePlaceholder: "Sélectionnez un service",
    serviceOptions: ["Domotique Loxone", "Panne / urgence", "Rénovation électrique", "Tableau électrique", "Éclairage / LED", "Borne de recharge VE", "Certificat / mise aux normes", "Autre"],
    messageLabel: "Message",
    messagePlaceholder: "Décrivez en détail ce dont vous avez besoin ou comment nous pouvons vous aider...",
    submitBtn: "Envoyer la demande",
    submittingBtn: "Envoi en cours...",
    consentLabelPrefix: "J'ai lu et j'accepte la ",
    privacyPolicyText: "Politique de Confidentialité",
    consentRequiredError: "Vous devez accepter la Politique de Confidentialité avant d'envoyer votre demande.",
    layerTitle: "Informations de base sur la protection des données",
    layerResponsibleLabel: "Responsable",
    layerResponsibleValue: "Carlos Leandro Claros Villa (Electro & Más)",
    layerPurposeLabel: "Finalité",
    layerPurposeValue: "traiter votre demande ou demande de devis",
    layerLegalBasisLabel: "Légitimation",
    layerLegalBasisValue: "votre consentement",
    layerRecipientsLabel: "Destinataires",
    layerRecipientsValue: "aucune cession de données à des tiers sauf obligation légale",
    layerRightsLabel: "Droits",
    layerRightsValue: "accès, rectification, suppression, opposition, limitation et portabilité en écrivant à electroymasbilbao@gmail.com",
    layerMoreInfo: "Plus d'informations dans la ",
    successHeading: "Demande envoyée !",
    successText: "Nous avons bien reçu votre message. Nous vous recontacterons dans les plus brefs délais.",
    successBtn: "Envoyer une autre demande"
  }
};
export default function PresupuestoGratuitoRespuestaRPida() {
  const [language] = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.es;
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formDef, setFormDef] = useState<any>(null);
  const [hpValue, setHpValue] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    email: "",
    servicio: "",
    mensaje: ""
  });
  useEffect(() => {
    const client = getWvcClient();
    if (!client?.pluginRequest) return;
    client.pluginRequest("wvc-forms/v1/forms/solicitud-presupuesto").then((data: any) => {
      if (data) setFormDef(data);
    }).catch((err: any) => {
      console.warn("Could not fetch form definition:", err);
    });
  }, []);
  const getFieldId = (key: string) => {
    if (formDef?.fields && Array.isArray(formDef.fields)) {
      const match = formDef.fields.find((f: any) => f.key === key);
      if (match?.id) return match.id;
    }
    return `f_${key}`;
  };
  const getSpanishServiceValue = () => {
    if (!formData.servicio) return "";
    if (translations.es.serviceOptions.includes(formData.servicio)) {
      return formData.servicio;
    }
    const idx = t.serviceOptions.indexOf(formData.servicio);
    if (idx !== -1) {
      return translations.es.serviceOptions[idx];
    }
    return formData.servicio;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!privacyAccepted) {
      setPrivacyError(true);
      return;
    }
    setPrivacyError(false);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setSubmitError("El servicio de envío no está disponible en este momento.");
      return;
    }
    const values: Record<string, string> = {
      [getFieldId("nombre")]: formData.nombre,
      [getFieldId("telefono")]: formData.telefono,
      [getFieldId("email")]: formData.email,
      [getFieldId("servicio")]: getSpanishServiceValue(),
      [getFieldId("mensaje")]: formData.mensaje
    };
    setIsSubmitting(true);
    try {
      const res = await client.pluginRequest("wvc-forms/v1/forms/solicitud-presupuesto/submit", {
        method: "POST",
        data: {
          values,
          token: formDef?.spam?.token,
          hp: formDef?.spam?.honeypot ? {
            [formDef.spam.honeypot.field]: hpValue
          } : undefined,
          meta: {
            source_url: typeof window !== "undefined" ? window.location.href : "",
            referrer: typeof document !== "undefined" ? document.referrer : ""
          }
        }
      });
      if (res?.success) {
        setSubmitted(true);
        setPrivacyAccepted(false);
        setPrivacyError(false);
        setFormData({
          nombre: "",
          telefono: "",
          email: "",
          servicio: "",
          mensaje: ""
        });
      } else if (res?.code === "frms_validation_failed") {
        const errorMessages = res.errors ? Object.values(res.errors).map((err: any) => err.message).filter(Boolean).join(" ") : "";
        setSubmitError(errorMessages || res?.message || "Por favor, revisa los campos requeridos.");
      } else {
        setSubmitError(res?.message || "No se pudo enviar la consulta. Inténtalo de nuevo.");
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Error al conectar con el servidor. Inténtalo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="presupuesto-gratuito-respuesta-rápida" data-nav="dark" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {/* Decorative technical hairline backdrop grid */}
      <div className="aria-hidden:true pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-20" aria-hidden="true" />

      <div className="relative z-10 max-w-[1904px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header block */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="mb-12 md:mb-16 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary border border-primary/20 text-primary font-mono text-xs uppercase tracking-widest mb-4">
              <span className="size-2 rounded-full bg-primary animate-pulse" />
              {t.eyebrow}
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight font-default text-foreground">
              {t.heading}
            </h2>
          </motion.div>

          {/* Grid layout: Contact Info + Form */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left Column: Direct contact info items */}
            <motion.div initial={{
            opacity: 0,
            x: -24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: "easeOut",
            delay: 0.1
          }} className="lg:col-span-5 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                {/* Móvil / WhatsApp */}
                <a href="tel:+34662498828" className="group flex items-start gap-4 p-5 rounded-xl bg-card border border-border transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-lg">
                  <div className="size-12 rounded-lg bg-secondary border border-border flex items-center justify-center text-primary shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <Smartphone className="size-5" />
                  </div>
                  <div>
                    <span className="text-xs font-mono uppercase text-muted-foreground tracking-wider block mb-1">
                      {t.mobileLabel}
                    </span>
                    <span className="font-mono text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                      +34 662 49 88 28
                    </span>
                  </div>
                </a>

                {/* Teléfono fijo */}
                <a href="tel:+34946424918" className="group flex items-start gap-4 p-5 rounded-xl bg-card border border-border transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-lg">
                  <div className="size-12 rounded-lg bg-secondary border border-border flex items-center justify-center text-primary shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <span className="text-xs font-mono uppercase text-muted-foreground tracking-wider block mb-1">
                      {t.landlineLabel}
                    </span>
                    <span className="font-mono text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                      +34 946 42 49 18
                    </span>
                  </div>
                </a>

                {/* Email */}
                <a href="mailto:electroymasbilbao@gmail.com" className="group flex items-start gap-4 p-5 rounded-xl bg-card border border-border transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-lg sm:col-span-2 lg:col-span-1">
                  <div className="size-12 rounded-lg bg-secondary border border-border flex items-center justify-center text-primary shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <Mail className="size-5" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs font-mono uppercase text-muted-foreground tracking-wider block mb-1">
                      {t.emailLabel}
                    </span>
                    <span className="font-mono text-base font-semibold text-foreground group-hover:text-primary transition-colors truncate block">
                      electroymasbilbao@gmail.com
                    </span>
                  </div>
                </a>
              </div>

              {/* Operational details card */}
              <div className="p-6 rounded-xl bg-card border border-border space-y-5">
                <div className="flex items-start gap-4">
                  <div className="size-10 rounded-lg bg-secondary border border-border flex items-center justify-center text-primary shrink-0">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <span className="text-xs font-mono uppercase text-muted-foreground tracking-wider block mb-0.5">
                      {t.workZoneLabel}
                    </span>
                    <p className="font-medium text-foreground">
                      {t.workZoneValue}
                    </p>
                  </div>
                </div>

                <div className="border-t border-border/60 pt-4 flex items-start gap-4">
                  <div className="size-10 rounded-lg bg-secondary border border-border flex items-center justify-center text-primary shrink-0">
                    <Clock className="size-5" />
                  </div>
                  <div>
                    <span className="text-xs font-mono uppercase text-muted-foreground tracking-wider block mb-0.5">
                      {t.hoursLabel}
                    </span>
                    <p className="font-medium text-foreground">
                      {t.hoursValue}
                    </p>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA Action */}
              <div className="pt-2">
                <Button asChild className="w-full h-14 rounded-full bg-[#25D366] text-white font-bold hover:bg-[#25D366]/90 shadow-lg text-base">
                  <Link to="https://wa.me/34662498828?text=Hola%2C%20quiero%20pedir%20presupuesto" newTab className="flex items-center justify-center gap-2">
                    <MessageSquare className="size-5" />
                    <span>{t.whatsappBtn}</span>
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Right Column: Contact & Quote Form */}
            <motion.div initial={{
            opacity: 0,
            x: 24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: "easeOut",
            delay: 0.2
          }} className="lg:col-span-7">
              <div className="p-6 sm:p-8 rounded-xl bg-card border border-border shadow-xl">
                {submitted ? <div className="py-12 text-center space-y-4">
                    <div className="size-16 rounded-full bg-primary/20 border border-primary/40 text-primary flex items-center justify-center mx-auto">
                      <CheckCircle2 className="size-8" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground">
                      {t.successHeading}
                    </h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      {t.successText}
                    </p>
                    <Button variant="secondary" onClick={() => {
                  setSubmitted(false);
                  setSubmitError(null);
                  setPrivacyAccepted(false);
                  setPrivacyError(false);
                }} className="mt-4 rounded-xl">
                      {t.successBtn}
                    </Button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-5">
                    {submitError && <div className="p-3.5 rounded-xl bg-destructive/15 border border-destructive/30 text-destructive text-sm font-medium">
                        {submitError}
                      </div>}

                    {/* Honeypot field if defined by plugin */}
                    {formDef?.spam?.honeypot && <div style={{
                  position: "absolute",
                  left: "-9999px"
                }} aria-hidden="true">
                        <input type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
                      </div>}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="nombre" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                          {t.nameLabel}
                        </Label>
                        <Input id="nombre" type="text" required placeholder={t.namePlaceholder} value={formData.nombre} onChange={e => setFormData({
                      ...formData,
                      nombre: e.target.value
                    })} className="h-12 bg-background/50 border-border rounded-xl focus:border-primary" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="telefono" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                          {t.phoneLabel}
                        </Label>
                        <Input id="telefono" type="tel" required placeholder={t.phonePlaceholder} value={formData.telefono} onChange={e => setFormData({
                      ...formData,
                      telefono: e.target.value
                    })} className="h-12 bg-background/50 border-border rounded-xl focus:border-primary font-mono" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                          {t.emailFieldLabel}
                        </Label>
                        <Input id="email" type="email" required placeholder={t.emailPlaceholder} value={formData.email} onChange={e => setFormData({
                      ...formData,
                      email: e.target.value
                    })} className="h-12 bg-background/50 border-border rounded-xl focus:border-primary" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="servicio" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                          {t.serviceLabel}
                        </Label>
                        <Select value={formData.servicio} onValueChange={value => setFormData({
                      ...formData,
                      servicio: value
                    })}>
                          <SelectTrigger className="h-12 w-full bg-background/50 border-border rounded-xl focus:border-primary text-foreground">
                            <SelectValue placeholder={t.selectServicePlaceholder} />
                          </SelectTrigger>
                          <SelectContent className="bg-popover border-border rounded-xl">
                            {t.serviceOptions.map((opt, i) => <SelectItem key={i} data-index={i} value={translations.es.serviceOptions[i]}>
                                {opt}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="mensaje" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        {t.messageLabel}
                      </Label>
                      <Textarea id="mensaje" required rows={4} placeholder={t.messagePlaceholder} value={formData.mensaje} onChange={e => setFormData({
                    ...formData,
                    mensaje: e.target.value
                  })} className="bg-background/50 border-border rounded-xl focus:border-primary min-h-[120px]" />
                    </div>

                    {/* RGPD-compliant Privacy Consent Checkbox */}
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-start gap-3">
                        <Checkbox id="privacy-consent" checked={privacyAccepted} onCheckedChange={checked => {
                      const val = checked === true;
                      setPrivacyAccepted(val);
                      if (val) setPrivacyError(false);
                    }} aria-invalid={privacyError} aria-describedby={privacyError ? "privacy-consent-error" : undefined} className="mt-0.5 border-border bg-background/50 focus-visible:border-primary" />
                        <label htmlFor="privacy-consent" className="text-xs sm:text-sm text-foreground/90 leading-tight cursor-pointer select-none">
                          <span>{t.consentLabelPrefix}</span>
                          <Link to="/PrivacyPolicyPage" newTab className="underline font-medium text-primary hover:text-primary/80 transition-colors">
                            {t.privacyPolicyText}
                          </Link>
                          <span className="text-destructive ml-1" aria-hidden="true">*</span>
                        </label>
                      </div>

                      {privacyError && <p id="privacy-consent-error" role="alert" className="text-xs text-destructive font-medium pl-7">
                          {t.consentRequiredError}
                        </p>}
                    </div>

                    {/* First-layer info text (RGPD primera capa informativa) */}
                    <div className="rounded-lg bg-secondary/50 border border-border/60 p-3 text-[11px] leading-relaxed text-muted-foreground space-y-1">
                      <p className="font-semibold text-foreground/80 text-[11px]">
                        {t.layerTitle}
                      </p>
                      <p>
                        <strong className="text-foreground/75 font-medium">{t.layerResponsibleLabel}:</strong> {t.layerResponsibleValue} · <strong className="text-foreground/75 font-medium">{t.layerPurposeLabel}:</strong> {t.layerPurposeValue} · <strong className="text-foreground/75 font-medium">{t.layerLegalBasisLabel}:</strong> {t.layerLegalBasisValue} · <strong className="text-foreground/75 font-medium">{t.layerRecipientsLabel}:</strong> {t.layerRecipientsValue} · <strong className="text-foreground/75 font-medium">{t.layerRightsLabel}:</strong> {t.layerRightsValue} · {t.layerMoreInfo}
                        <Link to="/PrivacyPolicyPage" newTab className="underline text-foreground/90 hover:text-primary transition-colors">
                          {t.privacyPolicyText}
                        </Link>.
                      </p>
                    </div>

                    <div className="pt-2">
                      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-full bg-primary text-primary-foreground font-bold hover:bg-primary/90 text-base shadow-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                        <Send className="size-5" />
                        <span>{isSubmitting ? t.submittingBtn || "Enviando..." : t.submitBtn}</span>
                      </Button>
                    </div>
                  </form>}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}